<template>
    <div class="row">
        <div class="col-md-8">
            <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing shadow">
                <form v-on:submit="submit">
                    <div class="p-0 d-flex justify-content-between gap-3">
                        <div class="localizations">
                            <div class="d-flex gap-3" v-if="locales.length > 1">
                                <country-flag v-for="locale in locales" :key="locale" :country="locale"
                                    :class="[{ 'opacity-25': lang !== locale }]" class="cursor-pointer"
                                    @click="changeLang(locale)" :title="$t(`feature.contents.$lang.${locale}`)" />
                            </div>
                        </div>
                        <div data-bs-toggle="modal" :data-bs-target="`#confirmModal-${block.id}`"
                            class="align-items-center bg-danger-subtle d-inline-flex fs-3 h-50 justify-content-center rounded-circle text-danger w-50 cursor-pointer">
                            <i class="ph-bold ph-x"></i>
                        </div>
                    </div>
                    <BlockForm v-bind:block="local_block" :update="true" v-bind:submitted="submitted" :lang="lang"
                        v-bind:errors="errors" />
                    <div class="col-12">
                        <div class="text-center w-100" v-if="loading">
                            <SpinnerLoading />
                        </div>
                        <div class="text-end w-100" v-else>
                            <div class="d-flex align-items-center justify-content-between gap-3 " v-if="edited">
                                <!-- DISCARD -->
                                <button type="button" @click="discard"
                                    class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium">
                                    <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                                    <span class="position-relative"> {{ $t('feature.contents.$blocks.discard_changes')
                                        }} </span>
                                </button>
                                <!-- SAVE -->
                                <button type="submit"
                                    class="ms-auto outlined default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16">
                                    {{ $t('feature.contents.$blocks.save') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <div class="col-md-4">
            <!--<div class="py-4" :class="[{ 'opacity-25': edited }]">
                <template v-if="block.type === 'text'">
                    <h5>{{ block.subtitle![lang] }}</h5>
                    <p>{{ block.value![lang] }}</p>
                </template>
                <template v-if="block.type === 'image'">
                    <img :src="block.value![lang]" :alt="block.subtitle![lang]">
                    <p class="text-muted mt-10"> {{ block.subtitle![lang] }}</p>
                </template>
                <template v-if="block.type === 'html'">
                    <h5>{{ block.subtitle![lang] }}</h5>
                    <div v-html="block.value![lang]"></div>
                </template>
            </div> -->
            <div class="py-4">
                <template v-if="block.type === 'text'">
                    <h5>{{ local_block.subtitle![lang] }}</h5>
                    <p>{{ local_block.value![lang] }}</p>
                </template>
                <template v-if="block.type === 'image'">
                    <img :src="local_block.value![lang]" :alt="local_block.subtitle![lang]">
                    <p class="text-muted mt-10"> {{ local_block.subtitle![lang] }}</p>
                </template>
                <!-- <template v-if="block.type === 'video'">
                    <iframe :src="local_block.value" frameborder="0" class="d-block h-100 ratio-16x9"></iframe>
                    <p class="text-muted mt-10"> {{ local_block.subtitle }}</p>
                </template> -->
                <template v-if="block.type === 'html'">
                    <h5>{{ local_block.subtitle![lang] }}</h5>
                    <div v-html="local_block.value![lang]"></div>
                </template>
            </div>
        </div>
    </div>

    <ConfirmModal :id="`confirmModal-${block.id}`" @confirm="deleteBlock()" :title="$t('cta.$delete.title')"
        :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import API from '@/services/api/api.service';
import getBase64 from '@/utils/methods/get_base_64';
import { ContentBlock, ContentBlockType } from '@/utils/models/content.model';
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import CountryFlag from 'vue-country-flag-next';
import { PropType } from 'vue';
import { defineComponent } from 'vue';

import BlockForm from "@/components/FK/Contents/Blocks/BlockForm.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { clone } from '@/utils/methods/form_utils';
import config from '@/utils/const/config';

export default defineComponent({
    props: {
        block: {
            type: Object as PropType<ContentBlock>,
            required: true
        },
        locale: {
            type: String,
            default: process.env.VUE_APP_LANG as string,
            required: true
        }
    },
    data() {
        return {
            lang: this.locale,
            local_block: clone(this.block),
            block_types: ['text', 'image', 'video', 'html'] as ContentBlockType[],
            submitted: false,
            loading: false,
            errors: undefined as any,
        }
    },
    computed: {
        edited() {
            return this.block.subtitle?.[this.lang] != this.local_block.subtitle?.[this.lang] || this.block.value?.[this.lang] != this.local_block.value?.[this.lang]
        },
        locales(): string[] {
            return config.available_locales;
        }
    },
    emits: ['update', 'delete', 'block', 'changeLang', 'click'],
    methods: {
        async fileUploaded($event) {
            this.local_block.value = await getBase64($event.target.files[0]);
        },
        submit(event) {
            this.errors = undefined;
            this.loading = true;
            event.preventDefault();
            console.log('SUBMIT');
            // API.updateContentBlock({ content_id: this.block.content_id!, block: this.local_block }).then((response) => {
            API.updateContentBlockLocalization({ lang: this.lang, content_id: this.block.content_id!, block: this.local_block }).then((response) => {
                console.log(response);
                if (response.data.data != undefined) {
                    this.local_block.subtitle[this.lang] = response.data.data.subtitle[this.lang];
                    this.local_block.value[this.lang] = response.data.data.value[this.lang];
                    this.$emit('update', clone(this.local_block));
                }
            }).catch((error) => {
                this.errors = error.response?.data?.errors;
            }).finally(() => {
                this.loading = false;
            });
        },
        deleteBlock() {
            this.loading = true;
            API.deleteContentBlock({ content_id: this.block.content_id!, block: this.block }).then((response) => {
                if (response.data.success === true) {
                    this.$emit('delete');
                }
            }).finally(() => {
                console.log("DELETED_BLOCK_ID", this.block.id!);
                this.loading = false;
            })
        },
        discard() {
            this.local_block = clone(this.block);
        },
        changeLang(lang: string) {
            this.lang = lang;
        }
    },
    components: {
        SpinnerLoading,
        ConfirmModal,
        BlockForm,
        CountryFlag
    },
    name: "ContentBlockForm",
});
</script>